/**
 * Print-Report-Utility für Produkte / Lagerbestand.
 *
 * Öffnet den Ausdruck bewusst in einem **eigenen Browser-Fenster** und
 * ruft dort ``window.print()`` auf — so wird garantiert kein App-Chrome
 * (Sidebar, Header, Filter-Controls, Zurück-Button) mitgedruckt und wir
 * müssen die Haupt-App nicht mit hoch-spezifischen ``@media print``-Regeln
 * zumüllen.
 *
 * Layout-Kontrakt:
 *   * A4 Querformat, 12 mm Rand
 *   * Kopfzeile: Bereich · Stand · aktive Filter
 *   * Tabelle wiederholt <thead> auf jeder Seite
 *   * ``tr { page-break-inside: avoid }`` verhindert Zeilen-Splits
 *   * dezente Rahmen, tabellarische Ziffern, kompakte Schrift (9pt)
 */

function escapeHtml(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatGermanDateTime(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * @param area     "Produkte" | "Lagerbestand"
 * @param filters  [{ label, value }]
 * @param columns  [{ label, align?: 'left'|'right'|'center' }]
 * @param rows     string[][]  — exakt so viele Zellen je Zeile wie ``columns``
 * @param resultCount  Optional — Anzahl exportierter Datensätze für die Kopfzeile
 */
export function openPrintReport({ area, filters = [], columns, rows, resultCount }) {
  const now = new Date();
  const filterRows = filters
    .map((f) =>
      `<div class="filter-row"><span class="label">${escapeHtml(f.label)}:</span> ${escapeHtml(f.value)}</div>`
    )
    .join("");

  const alignClass = (a) => (a === "right" ? "num" : a === "center" ? "center" : "");
  const headerCells = columns
    .map((c) => `<th class="${alignClass(c.align)}">${escapeHtml(c.label)}</th>`)
    .join("");
  const bodyRows = rows
    .map((r) => {
      const tds = r
        .map((cell, i) => {
          const cls = alignClass(columns[i]?.align);
          return `<td${cls ? ` class="${cls}"` : ""}>${escapeHtml(cell)}</td>`;
        })
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("");

  const countLine =
    typeof resultCount === "number"
      ? `<div class="filter-row"><span class="label">Datensätze:</span> ${resultCount}</div>`
      : "";

  // Einfaches, statisches, selbstenthaltenes HTML — keine externen
  // Fonts oder Scripts. Damit lädt der Ausdruck offline und ohne CORS-
  // Themen. Print-Trigger wird nach ``load`` gesetzt, mit kurzer
  // Verzögerung, damit Webkit-basierte Browser Layout finalisieren.
  const html = `<!DOCTYPE html>
<html lang="de"><head><meta charset="utf-8"/>
<title>${escapeHtml(area)}</title>
<style>
@page { size: A4 landscape; margin: 12mm; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; font-size: 9.5pt; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.report-header { border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 10px; }
.brand { font-size: 8.5pt; letter-spacing: 3px; text-transform: uppercase; color: #555; }
.title { font-size: 18pt; font-weight: 800; margin-top: 2px; }
.stand { margin-top: 4px; font-size: 9pt; color: #333; }
.stand .label { color: #666; }
.filter-block { margin-top: 6px; font-size: 8.5pt; color: #222; }
.filter-block > .caption { color: #666; margin-bottom: 2px; }
.filter-row { padding-left: 10px; }
.filter-row .label { color: #666; }
table { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-top: 4px; }
thead { display: table-header-group; }
tfoot { display: table-row-group; }
th, td { padding: 4px 6px; vertical-align: top; }
th { text-align: left; border-bottom: 1.2px solid #111; background: #f2f2f2; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; font-size: 7.8pt; }
td { border-bottom: 0.5px solid #d5d5d5; }
tr { page-break-inside: avoid; }
.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.center { text-align: center; }
.footer { margin-top: 14px; font-size: 7.5pt; color: #888; text-align: right; }
.print-toolbar { position: fixed; top: 10px; right: 10px; background: #111; color: #fff; padding: 8px 12px; border-radius: 3px; font-family: inherit; font-size: 10pt; }
.print-toolbar button { background: #06b6d4; color: #041014; border: 0; padding: 6px 12px; margin-left: 6px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; border-radius: 2px; }
.print-toolbar button.secondary { background: #333; color: #eee; }
@media print { .print-toolbar { display: none; } }
</style>
</head>
<body>
<div class="print-toolbar">
  <span>Vorschau — bitte über den Drucken-Dialog Ihres Browsers ausdrucken.</span>
  <button type="button" id="loop-print-btn">Drucken</button>
  <button type="button" id="loop-close-btn" class="secondary">Schließen</button>
</div>
<div class="report-header">
  <div class="title">${escapeHtml(area)}</div>
  <div class="stand"><span class="label">Stand:</span> ${escapeHtml(formatGermanDateTime(now))}</div>
  <div class="filter-block">
    <div class="caption">Filter:</div>
    ${filterRows}
    ${countLine}
  </div>
</div>
<table>
  <thead><tr>${headerCells}</tr></thead>
  <tbody>${bodyRows}</tbody>
</table>
</body></html>`;

  // Zuerst reguläres Popup-Fenster versuchen (bei User-Gesten in
  // normalen Browsern erlaubt). Wenn der Blocker anspringt oder wir in
  // einer Umgebung ohne Popup-Support laufen (Playwright headless etc.),
  // fällt der Fluss auf einen versteckten Print-iFrame zurück.
  //
  // WICHTIG (CSP): Das erzeugte HTML enthält absichtlich KEIN inline
  // ``onclick=…`` / ``onload=…`` / ``<script>``. Produktiv-Nginx setzt
  // eine strikte Content-Security-Policy ohne ``'unsafe-inline'``, die
  // inline Event-Handler blockiert. Alle Klick-Listener werden vom
  // Parent-Kontext aus per DOM-API angehängt — dieser JavaScript-Code
  // läuft im Ursprungs-Origin und wird nicht durch das Popup-CSP geblockt.
  let w = null;
  try {
    w = window.open("about:blank", "_blank", "width=1200,height=850");
  } catch (_e) { w = null; }

  if (w && w.document) {
    w.document.open();
    w.document.write(html);
    w.document.close();

    // Toolbar-Buttons per addEventListener anhängen (CSP-safe, iOS-safe
    // — echter User-Klick auf „Drucken" ist eine gültige User-Activation
    // für ``window.print()`` im Safari/iOS).
    const attachToolbar = () => {
      try {
        const printBtn = w.document.getElementById("loop-print-btn");
        const closeBtn = w.document.getElementById("loop-close-btn");
        if (printBtn) {
          printBtn.addEventListener("click", () => {
            try { w.focus(); w.print(); } catch (_e) { /* Nutzer öffnet manuell */ }
          });
        }
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            try { w.close(); } catch (_e) { /* Popup ggf. bereits zu */ }
          });
        }
      } catch (_e) { /* Popup wurde vor Attach geschlossen — ignorieren */ }
    };
    attachToolbar();

    // Automatischen Systemdruck-Aufruf möglichst innerhalb der
    // ursprünglichen User-Activation ausführen. iOS/Safari akzeptiert
    // ``print()`` synchron oder in einem sehr kurzen Timeout — längere
    // Ketten (load-Event → setTimeout 150) verlieren dort die Activation.
    try { w.focus(); w.print(); } catch (_e) { /* Nutzer klickt Toolbar */ }
    return { ok: true, mode: "window" };
  }

  // Fallback: iFrame ans Ende des DOM anhängen, ins iFrame-Document
  // schreiben, ``print()`` auf dem iFrame ausführen. Kein App-Chrome
  // wird gedruckt, weil das iFrame die ganze druckbare Fläche belegt
  // und wir per ``@media print { body > *:not(#print-frame) { display:none } }``
  // alles andere ausblenden.
  const frameId = "loop-print-frame";
  document.getElementById(frameId)?.remove();
  const iframe = document.createElement("iframe");
  iframe.id = frameId;
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);

  // Print-Mode-CSS ins Haupt-Dokument einspielen, damit während des
  // Druckens NUR der iFrame-Inhalt zu sehen ist.
  let styleTag = document.getElementById("loop-print-mode-style");
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = "loop-print-mode-style";
    styleTag.textContent = `@media print {
      body > *:not(#${frameId}) { display: none !important; }
      #${frameId} { position: fixed !important; inset: 0 !important; width: 100% !important; height: 100% !important; visibility: visible !important; }
    }`;
    document.head.appendChild(styleTag);
  }

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();

  // Toolbar-Buttons auch im iFrame per DOM-API verdrahten — kein
  // Inline-Handler, damit die App-CSP das Print-Dokument nicht
  // blockiert. Im iFrame-Mode wird die Toolbar in der Praxis kaum
  // sichtbar, weil der Ausdruck sofort ausgelöst wird und das iFrame
  // ansonsten unsichtbar (0×0) hängt — wir wollen aber trotzdem eine
  // fachlich saubere Manual-Trigger-Option, falls jemand das iFrame
  // per DevTools sichtbar schaltet.
  try {
    const iprint = doc.getElementById("loop-print-btn");
    const iclose = doc.getElementById("loop-close-btn");
    if (iprint) iprint.addEventListener("click", () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_e) { /* ignore */ }
    });
    if (iclose) iclose.addEventListener("click", () => {
      try { iframe.remove(); } catch (_e) { /* ignore */ }
    });
  } catch (_e) { /* iFrame doc unavailable — nur der Auto-Trigger unten wirkt */ }

  // Synchroner Print-Aufruf im User-Activation-Kontext. Kein
  // setTimeout-Delay (das würde die Activation auf iOS/Safari
  // verlieren).
  try {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  } catch (_e) { /* Nutzer druckt manuell via Toolbar */ }
  return { ok: true, mode: "iframe" };
}
